import styled from 'styled-components';

const variants = {
  primary: {
    background: {
      active: '#4318cd',
      disable: '#d5e5f0',
      enable: '#5b36f2',
      hover: '#745ef6'
    },
    color: { enable: '#ffffff', disable: '#8090bb' },
    border: {
      active: '#4318cd',
      disable: '#d5e5f0',
      enable: '#5b36f2',
      hover: '#745ef6'
    }
  },
  secondary: {
    background: {
      active: '#c6c0fc',
      disable: '#ffffff',
      enable: '#ffffff',
      hover: '#e2dffd'
    },
    color: { enable: '#5b36f2', disable: '#8090bb' },
    border: {
      active: '#5b36f2',
      disable: '#8090bb',
      enable: '#5b36f2',
      hover: '#5b36f2'
    }
  },
  tertiary: {
    background: {
      active: '#c6c0fc',
      disable: 'transparent',
      enable: 'transparent',
      hover: '#e2dffd'
    },
    color: { enable: '#5b36f2', disable: '#8090BB' },
    border: {
      active: '#c6c0fc',
      disable: 'transparent',
      enable: 'transparent',
      hover: '#e2dffd'
    }
  },
  tooltip: {
    background: {
      active: '#4318cd',
      disable: 'transparent',
      enable: 'transparent',
      hover: '#745ef6'
    },
    color: { enable: '#ffffff', disable: '#ffffff' },
    border: {
      active: '#4318cd',
      disable: 'transparent',
      enable: 'transparent',
      hover: '#745ef6'
    }
  }
};

const onPropsValue = (
  key: 'background' | 'border' | 'color',
  value: 'active' | 'disable' | 'enable' | 'hover',
  variant: 'primary' | 'secondary' | 'tertiary' | 'tooltip'
) => (variants[variant][key] as any)[value];

const SButton = styled.button<{
  fullWidth?: string;
  isIcon?: boolean;
  size: 'small' | 'medium';
  variant: 'primary' | 'secondary' | 'tertiary' | 'tooltip';
}>`
  user-select: none;
  height: ${({ size }) => (size === 'small' ? '32px' : '44px')};
  width: ${({ isIcon, size }) =>
    isIcon ? (size === 'small' ? '32px' : '44px') : '100%'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ size }) => (size === 'small' ? '6px' : '10px')};
  max-width: ${({ isIcon, size }) =>
    isIcon ? (size === 'small' ? '32px' : '44px') : 'max-content'};
  padding: 0 23px;
  border-radius: 4px;
  font-size: ${({ size }) => (size === 'small' ? '12px' : '14px')};
  font-weight: 700;
  line-height: ${({ size }) => (size && size === 'small' ? '16px' : '20px')};
  text-align: center;
  border-style: solid;
  border-width: 1px;
  border-color: ${({ variant }) => onPropsValue('border', 'enable', variant)};
  color: ${({ variant }) => onPropsValue('color', 'enable', variant)};
  background-color: ${({ variant }) =>
    onPropsValue('background', 'enable', variant)};

  &:has(> svg) {
    padding: ${({ isIcon }) => (isIcon ? '0' : '0 23px 0 15px')};
  }

  > svg {
    height: ${({ size }) => (size === 'small' ? '14px' : '18px')};
    max-height: ${({ size }) => (size === 'small' ? '14px' : '18px')};
    max-width: ${({ size }) => (size === 'small' ? '14px' : '18px')};
    min-height: ${({ size }) => (size === 'small' ? '14px' : '18px')};
    min-width: ${({ size }) => (size === 'small' ? '14px' : '18px')};
    width: ${({ size }) => (size === 'small' ? '14px' : '18px')};
  }

  &[data-active='true'] {
    border-color: ${({ variant }) => onPropsValue('border', 'hover', variant)};
    background-color: ${({ variant }) =>
      onPropsValue('background', 'hover', variant)};
  }

  &:hover {
    border-color: ${({ variant }) => onPropsValue('border', 'hover', variant)};
    background-color: ${({ variant }) =>
      onPropsValue('background', 'hover', variant)};
  }

  &:focus-visible,
  &:active {
    border-color: ${({ variant }) => onPropsValue('border', 'active', variant)};
    background-color: ${({ variant }) =>
      onPropsValue('background', 'active', variant)};
  }

  &:disabled {
    cursor: not-allowed;
    border-color: ${({ variant }) =>
      onPropsValue('border', 'disable', variant)};
    background-color: ${({ variant }) =>
      onPropsValue('background', 'disable', variant)};
    color: ${({ variant }) => onPropsValue('color', 'disable', variant)};
  }

  @media (max-width: ${({ fullWidth }) => fullWidth ?? '0'}) {
    max-width: 100%;
  }
`;

export { SButton };
